import { z } from 'zod';

// Base bilingual field schema
export const bilingualFieldSchema = z.object({
  english: z.string().min(1, 'English field is required'),
  arabic: z.string().min(1, 'Arabic field is required'),
});

// Base form schema for simple forms (departments and titles)
export const baseFormSchema = z.object({
  nameEnglish: z.string().min(1, 'English name is required'),
  nameArabic: z.string().min(1, 'Arabic name is required'),
});

export const touchpointFormSchema = z.object({
  nameEnglish: z.string().min(1, 'English name is required'),
  nameArabic: z.string().min(1, 'Arabic name is required'),
});
// Sub-touchpoint form schema
export const subTouchpointFormSchema = z.object({
  nameEnglish: z.string().min(1, 'English name is required'),
  nameArabic: z.string().min(1, 'Arabic name is required'),
  parentTouchpointId: z.string().min(1, 'Parent touchpoint is required'),
});
export const addStandardFormSchema = z.object({
  department: z.string().min(1, 'Department is required'),
  touchpoint: z.string().min(1, 'Touchpoint is required'),
  subtouchpoint: z.string().optional(),
  title: z.string().min(1, 'Title is required'),
  standardTextEnglish: z
    .string()
    .min(10, 'Standard text in English must be at least 10 characters'),
  standardTextArabic: z
    .string()
    .min(10, 'Standard text in Arabic must be at least 10 characters'),
  answerType: z
    .enum(['YNNA', 'YES_NO', 'RATING_5', 'RATING_10', 'TEXT'])
    .default('YNNA'),
  commentRequired: z.boolean().default(false),
});
// Edit template name schema with bilingual support
export const editTemplateNameSchema = z.object({
  nameEnglish: z
    .string()
    .min(1, 'English name is required')
    .max(50, 'English name must be less than 50 characters'),
  nameArabic: z
    .string()
    .min(1, 'Arabic name is required')
    .max(50, 'Arabic name must be less than 50 characters'),
});

// Edit property type schema with icon and color
export const editPropertyTypeSchema = z.object({
  nameEnglish: z
    .string()
    .min(1, 'English name is required')
    .max(100, 'English name must be less than 100 characters'),
  nameArabic: z
    .string()
    .min(1, 'Arabic name is required')
    .max(100, 'Arabic name must be less than 100 characters'),
  icon: z.string().min(1, 'Icon is required'),
  color: z.string().min(1, 'Color is required'),
});

// Add property type schema
export const addPropertyTypeSchema = z.object({
  nameEnglish: z
    .string()
    .min(1, 'English name is required')
    .max(100, 'English name must be less than 100 characters'),
  nameArabic: z
    .string()
    .min(1, 'Arabic name is required')
    .max(100, 'Arabic name must be less than 100 characters'),
  icon: z.string().min(1, 'Icon is required'),
  color: z.string().min(1, 'Color is required'),
});

// Schema for adding new department
export const addDepartmentSchema = z.object({
  name: bilingualFieldSchema,
});

// Schema for adding new touchpoint
export const addTouchpointSchema = z.object({
  name: bilingualFieldSchema,
});

// Schema for adding new sub-touchpoint (requires parent touchpoint)
export const addSubTouchpointSchema = z.object({
  name: bilingualFieldSchema,
  parentTouchpointId: z.string().min(1, 'Parent touchpoint is required'),
});

// Schema for adding new title
export const addTitleSchema = z.object({
  name: bilingualFieldSchema,
});

// Union schema for different column types
export const addItemSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('department'), data: addDepartmentSchema }),
  z.object({ type: z.literal('touchpoint'), data: addTouchpointSchema }),
  z.object({ type: z.literal('subtouchpoint'), data: addSubTouchpointSchema }),
  z.object({ type: z.literal('title'), data: addTitleSchema }),
]);
export type BaseFormData = z.infer<typeof baseFormSchema>;

// Touchpoint form schema

export type TouchpointFormData = z.infer<typeof touchpointFormSchema>;

export type SubTouchpointFormData = z.infer<typeof subTouchpointFormSchema>;

// Add standard form schema - comprehensive form for creating new standards

export type AddStandardFormData = z.infer<typeof addStandardFormSchema>;

export type EditTemplateNameData = z.infer<typeof editTemplateNameSchema>;
export type EditPropertyTypeData = z.infer<typeof editPropertyTypeSchema>;
export type AddPropertyTypeData = z.infer<typeof addPropertyTypeSchema>;

// Types derived from schemas
export type BilingualField = z.infer<typeof bilingualFieldSchema>;
export type AddDepartmentData = z.infer<typeof addDepartmentSchema>;
export type AddTouchpointData = z.infer<typeof addTouchpointSchema>;
export type AddSubTouchpointData = z.infer<typeof addSubTouchpointSchema>;
export type AddTitleData = z.infer<typeof addTitleSchema>;
export type AddItemData = z.infer<typeof addItemSchema>;

// Enhanced template interfaces with bilingual support
export interface BilingualTemplate {
  id?: string;
  propertyType: BilingualField;
  departments?: Array<{
    id?: string;
    name: BilingualField;
    touchpoints?: Array<{
      id?: string;
      name: BilingualField;
      code: string;
      titles?: Array<{
        id?: string;
        name: BilingualField;
      }>;
      subTouchpoints?: Array<{
        id?: string;
        name: BilingualField;
        code: string;
        parentTouchpointId: string;
      }>;
    }>;
  }>;
}
